import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import {
  Card,
  CardContent,
} from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import {
  Target,
  Plus,
  Loader2,
  Calendar,
  Clock,
} from "lucide-react"
import { listPrepPlans, type PrepPlanListItem } from "@/api/examPrep"
import { ROUTES } from "@/constants/routes"

const STATUS_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  active: { bg: "bg-green-100", text: "text-green-700", label: "进行中" },
  completed: { bg: "bg-blue-100", text: "text-blue-700", label: "已完成" },
  paused: { bg: "bg-gray-100", text: "text-gray-600", label: "已暂停" },
  abandoned: { bg: "bg-red-100", text: "text-red-700", label: "已放弃" },
}

function getStatusStyle(status: string) {
  return STATUS_STYLES[status] ?? STATUS_STYLES.active
}

function getDaysRemaining(examDate: string): number | null {
  if (!examDate) return null
  const exam = new Date(examDate)
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  exam.setHours(0, 0, 0, 0)
  const diff = Math.ceil((exam.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
  return diff >= 0 ? diff : null
}

export default function PlanListPage() {
  const [plans, setPlans] = useState<PrepPlanListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadPlans()
  }, [])

  async function loadPlans() {
    setLoading(true)
    setError(null)
    try {
      const data = await listPrepPlans()
      setPlans(data ?? [])
    } catch (err) {
      console.error("Failed to load plans:", err)
      setError("加载备考计划失败，请稍后重试")
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto py-8 px-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <p className="text-red-500 mb-4">{error}</p>
              <Button onClick={loadPlans}>重新加载</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">自助备考</h1>
          <p className="text-slate-500 mt-1">为你的竞赛考试制定备考计划</p>
        </div>
        <Link to={ROUTES.EXAM_PREP_NEW}>
          <Button>
            <Plus className="w-4 h-4 mr-2" />
            新建备考计划
          </Button>
        </Link>
      </div>

      {plans.length > 0 ? (
        <div className="grid gap-4">
          {plans.map((plan) => {
            const statusStyle = getStatusStyle(plan.status)
            const daysRemaining = getDaysRemaining(plan.exam_date)
            const progressPct = plan.total_weeks > 0
              ? Math.round((plan.current_week / plan.total_weeks) * 100)
              : 0

            return (
              <Link key={plan.id} to={`${ROUTES.EXAM_PREP}/${plan.id}`}>
                <Card className="cursor-pointer hover:shadow-md transition-shadow">
                  <CardContent className="py-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-grow min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-lg text-slate-800 truncate">
                            {plan.exam_name}
                          </h3>
                          <span
                            className={`text-xs px-2 py-0.5 rounded ${statusStyle.bg} ${statusStyle.text}`}
                          >
                            {statusStyle.label}
                          </span>
                        </div>

                        <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                          {plan.exam_date ? (
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              <span>考试日期：{plan.exam_date}</span>
                              {daysRemaining !== null && daysRemaining > 0 && (
                                <span className="ml-1 text-orange-600 font-medium">
                                  还剩 {daysRemaining} 天
                                </span>
                              )}
                            </div>
                          ) : (
                            <span>考试日期待定</span>
                          )}
                          <div className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            <span>已完成 {plan.completed_days} 天</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <Progress value={progressPct} className="flex-grow h-2" />
                          <span className="text-xs text-slate-500 whitespace-nowrap">
                            第 {plan.current_week}/{plan.total_weeks} 周
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <Target className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">
                还没有备考计划
              </h3>
              <p className="text-slate-500 mb-6">
                创建你的第一个备考计划，AI 将为你量身定制学习方案
              </p>
              <Link to={ROUTES.EXAM_PREP_NEW}>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  开始备考
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
